import { Search } from 'lucide-react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { ExportIcon } from '../Icons'
import { DataTable } from '../data-table'
import { useState } from 'react'
import { SEARCH_DEBOUNCE_TIMEOUT } from '@/constants'
import useDebounce from '@/hooks/use-debounce'
import { showErrorToast, showSuccessToast } from '../ui/toast'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  fetchAttendeeBookings,
  getAttendeeBookingExport,
  downloadAttendeeConsentForm,
  type AttendeesParams,
} from '@/api/attendeeApi'
import {
  handleAxiosError,
  safeDownloadFile,
  transformAttendeeBookingData,
} from '@/utils/helper'
import type { SortingState } from 'node_modules/@tanstack/table-core/build/lib/features/RowSorting'
import type {
  AttendeeBookingsSchema,
  DisplayAttendeeBookingsSchema,
} from '@/schemas/attendee.schema'
import { ATTENDEE_QUERY_KEYS } from '@/api/queryKeys'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { attendeePersona, attendeeSkiExperience } from '@/constants/attendee'
import {
  getEventAttendeeColumns,
  type EventAttendeeDetails,
} from './event-attendee-columns'

interface EventAttendeesTableProps {
  eventId?: string
  externalSearchQuery?: string
}

export function EventAttendeesTable({
  eventId,
  externalSearchQuery,
}: Readonly<EventAttendeesTableProps>) {
  const [searchQuery, setSearchQuery] = useState('')
  const effectiveSearchQuery = externalSearchQuery ?? searchQuery
  const debouncedSearchTerm = useDebounce(
    effectiveSearchQuery,
    SEARCH_DEBOUNCE_TIMEOUT
  )
  const [sorting, setSorting] = useState<SortingState>([])
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })
  const [selectedExperience, setSelectedExperience] = useState<string>('all')
  const [selectedPersona, setSelectedPersona] = useState<string>('all')
  const [downloadingAttendeeDetails, setDownloadingAttendeeDetails] =
    useState<EventAttendeeDetails | null>(null)

  const {
    data: bookingsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      ATTENDEE_QUERY_KEYS.attendee_bookings,
      pageSize,
      currentPage,
      eventId,
      sorting,
      debouncedSearchTerm,
      selectedExperience,
      selectedPersona,
    ],
    queryFn: () =>
      fetchAttendeeBookings({
        limit: pageSize,
        page: currentPage,
        sort: sorting,
        eventId: eventId || undefined,
        search: debouncedSearchTerm || undefined,
        experience:
          selectedExperience && selectedExperience !== 'all'
            ? selectedExperience
            : undefined,
        persona:
          selectedPersona && selectedPersona !== 'all'
            ? selectedPersona
            : undefined,
      }),
  })

  const { mutate: exportBookings, isPending: isExportingBookings } =
    useMutation({
      mutationFn: getAttendeeBookingExport,
      onSuccess: blob =>
        safeDownloadFile({
          data: blob,
          fileName: 'attendee-bookings-export.csv',
        }),
      onError: err => {
        handleAxiosError('Failed to export bookings. Please try again.', err)
      },
    })

  const { mutate: downloadConsentForm } = useMutation({
    mutationFn: (consentFormKey: string) =>
      downloadAttendeeConsentForm(consentFormKey),
    onSuccess: data => {
      safeDownloadFile({
        data: data,
        fileName: `consent_form_${downloadingAttendeeDetails?.id}-${downloadingAttendeeDetails?.name.replaceAll(/\s+/g, '_')}.pdf`,
      })
      showSuccessToast('Consent form downloaded successfully')
      setDownloadingAttendeeDetails(null)
    },
    onError: err => {
      handleAxiosError(
        'Failed to download consent form. Please try again.',
        err
      )
      setDownloadingAttendeeDetails(null)
    },
  })

  const handleDownloadConsentForm = (details: EventAttendeeDetails) => {
    setDownloadingAttendeeDetails(details)
    downloadConsentForm(details.consentFormUrl)
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const handleExperienceChange = (value: string) => {
    setSelectedExperience(value)
    setCurrentPage(1)
  }

  const handlePersonaChange = (value: string) => {
    setSelectedPersona(value)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleExportClick = () => {
    if (displayBookings.length === 0) {
      showErrorToast(
        'No data available for export',
        'Please adjust filters or try again later.'
      )
      return
    }

    const params: AttendeesParams = {
      sort: sorting,
      eventId: eventId,
      search: debouncedSearchTerm || undefined,
      experience:
        selectedExperience && selectedExperience !== 'all'
          ? selectedExperience
          : undefined,
      persona:
        selectedPersona && selectedPersona !== 'all'
          ? selectedPersona
          : undefined,
    }

    exportBookings(params)
  }

  const rawBookings: AttendeeBookingsSchema[] = bookingsData?.data?.result || []
  const displayBookings: DisplayAttendeeBookingsSchema[] = rawBookings.map(
    transformAttendeeBookingData
  )
  const totalItems = bookingsData?.data?.totalCount || 0
  const totalPages =
    bookingsData?.data?.totalPages || Math.ceil(totalItems / pageSize)

  return (
    <div className="relative mt-4">
      <div className="flex items-center justify-between my-4 px-6">
        <h2 className="text-xl font-bold text-ss-neutral-950">
          Event Attendee Bookings
        </h2>

        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 gap-2">
          <Select
            value={selectedExperience}
            onValueChange={handleExperienceChange}
          >
            <SelectTrigger className="w-[180px] h-9 rounded-lg bg-white border-ss-neutral-200">
              <SelectValue placeholder="Select Experience" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(attendeeSkiExperience).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedPersona} onValueChange={handlePersonaChange}>
            <SelectTrigger className="w-[200px] h-9 rounded-lg bg-white border-ss-neutral-200">
              <SelectValue placeholder="Select Persona" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(attendeePersona).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {externalSearchQuery === undefined && (
            <div className="relative w-[230px]">
              <Search
                id="attendee-bookings-search"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ss-neutral-600"
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder="Search Bookings..."
                value={searchQuery}
                onChange={e => {
                  handleSearchChange(e.target.value)
                }}
                className="pl-10 h-9 rounded-lg bg-white text-ss-neutral-900 border-ss-neutral-200 
        focus:border-ss-primary-500 focus:ring-1 focus:ring-ss-primary-500
        placeholder:text-ss-neutral-400"
                aria-label="Search bookings input"
              />
            </div>
          )}

          <Button
            variant="default"
            onClick={handleExportClick}
            aria-label="Export bookings as CSV"
            disabled={isExportingBookings}
          >
            <ExportIcon className="h-4 w-4" aria-hidden="true" />
            Export Bookings CSV
          </Button>
        </div>
      </div>

      <DataTable
        data={displayBookings}
        columns={getEventAttendeeColumns(
          handleDownloadConsentForm,
          downloadingAttendeeDetails?.id
        )}
        sorting={sorting}
        setSorting={setSorting}
        pagination={pagination}
        setPagination={setPagination}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        showFilter={false}
        showExport={false}
        isLoading={isLoading}
        emptyState={{
          image: '/auth/empty-eventlist.svg',
          title: 'No bookings yet',
          subtitle:
            'Event attendee bookings will appear here once attendees book experiences.',
        }}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        error={error?.message || null}
      />
    </div>
  )
}
